import {isFilled} from '@prismicio/client'
import type {LinkField, LinkToMediaField} from '@prismicio/client'

/**
 * Safely extracts the locale from a Prismic LinkField.
 * Returns the locale if the link is filled and has a lang property, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkField to extract locale from
 * @param fallback - The fallback locale to use if link is not filled or has no lang property (defaults to 'en-us')
 * @returns The locale string
 *
 * @example
 * const locale = getLinkLocale(slice.primary.link)
 * <Link href={linkResolver(link)} locale={locale}>Click me</Link>
 */
export function getLinkLocale(link: LinkField | undefined | null, fallback: string = 'en-us'): string {
  return isFilled.link(link) && 'lang' in link ? link.lang : fallback
}

/**
 * Safely extracts the URL from a Prismic LinkToMediaField.
 * Returns the URL if the media link is filled, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkToMediaField to extract URL from
 * @param fallback - The fallback URL to use if link is not filled (defaults to empty string)
 * @returns The URL string
 *
 * @example
 * const videoUrl = getMediaLinkUrl(slice.primary.video)
 * <VideoPlayer src={videoUrl} />
 */
export function getMediaLinkUrl(link: LinkToMediaField | undefined | null, fallback: string = ''): string {
  return isFilled.linkToMedia(link) ? link.url : fallback
}

/**
 * Safely extracts the URL from a Prismic LinkToMediaField.
 * Returns the URL if the media link is filled, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkToMediaField to extract URL from
 * @param fallback - The fallback URL to use if link is not filled (defaults to empty string)
 * @returns The URL string
 *
 * @example
 * const videoUrl = getMediaLinkUrl(slice.primary.video)
 * <VideoPlayer src={videoUrl} />
 */
export function getLinkUrl(link: LinkField | undefined | null, fallback: string = ''): string {
  return isFilled.link(link) && link.url ? link.url : fallback
}

/**
 * Safely extracts the target from a Prismic LinkField.
 * Returns the target if the link is filled and has a target property, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkField to extract target from
 * @param fallback - The fallback target to use if link is not filled or has no target property (defaults to '_self')
 * @returns The target string ('_self', '_blank', etc.)
 *
 * @example
 * const target = getLinkTarget(tile.link_url)
 * <Link href={url} target={target}>Click me</Link>
 */
export function getLinkTarget(link: LinkField | undefined | null, fallback: string = '_self'): string {
  return isFilled.link(link) && 'target' in link ? link.target ?? fallback : fallback
}
